import React, { lazy, Suspense, useEffect } from "react";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import ErrorBoundary from "./ErrorBoundary";
import MetaTags from "./components/SEO/MetaTags";

// Environment verification on app start
import { logEnvironmentStatus } from './utils/envValidation';

// Validate and log environment status on startup
if (process.env.NODE_ENV === 'development') {
  logEnvironmentStatus();
}

// Eagerly load critical components for fast initial load
import ProtectedRoute from "./components/auth/ProtectedRoute";
import BeattAdminRoute from "./components/auth/BeattAdminRoute";
import HomeSimple from "./pages/HomeSimple";
import Login from "./pages/Login";

// Loading component for better UX
const LoadingFallback = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    fontSize: '1.2rem',
    color: '#666'
  }}>
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
  </div>
);

// Lazy load non-critical pages
const About = lazy(() => import("./pages/About"));
const Signup = lazy(() => import("./pages/Signup"));
const EmailVerification = lazy(() => import("./pages/EmailVerification"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Pricing = lazy(() => import("./pages/Pricing"));
const QuickCheck = lazy(() => import("./pages/QuickCheck"));
const QuickCheckIA = lazy(() => import("./pages/QuickCheckIA"));
const QuickCheckPublicReport = lazy(() => import("./pages/QuickCheckPublicReport"));
const QuickCheckIAPublicReport = lazy(() => import("./pages/QuickCheckIAPublicReport"));

// Lazy load dashboard with prefetch
const RoleBasedDashboard = lazy(() => 
  import(/* webpackPrefetch: true */ "./components/dashboard/RoleBasedDashboard")
);

// Test page for authentication
const TestAuth = lazy(() => import("./pages/TestAuth"));

// Lazy load assessment module
const Assessments = lazy(() =>
  import(/* webpackChunkName: "assessment" */ "./pages/Assessments")
);
const AssessmentCreator = lazy(() => 
  import(/* webpackChunkName: "assessment" */ "./pages/AssessmentCreator")
);
const AssessmentDistribute = lazy(() => 
  import(/* webpackChunkName: "assessment" */ "./pages/AssessmentDistribute")
);
const AssessmentTake = lazy(() => 
  import(/* webpackChunkName: "assessment" */ "./pages/AssessmentTake")
);
const AssessmentResultsPage = lazy(() => 
  import(/* webpackChunkName: "assessment" */ "./pages/AssessmentResults")
);

// Lazy load management modules
const OKRManager = lazy(() =>
  import(/* webpackChunkName: "management" */ "./pages/OKRManager")
);
const PDIManager = lazy(() =>
  import(/* webpackChunkName: "management" */ "./pages/PDIManager")
);
const FeedbackManager = lazy(() =>
  import(/* webpackChunkName: "management" */ "./pages/FeedbackManager")
);
const SurveyManager = lazy(() =>
  import(/* webpackChunkName: "management" */ "./pages/SurveyManager")
);

// Lazy load Blog
const Blog = lazy(() =>
  import(/* webpackChunkName: "blog" */ "./pages/Blog")
);

// Lazy load PDI module
const PDI = lazy(() => 
  import(/* webpackChunkName: "pdi" */ "./pages/PDI")
);
const PDICreate = lazy(() => 
  import(/* webpackChunkName: "pdi" */ "./pages/PDICreate")
);

// Lazy load analytics and reporting
const AnalyticsDashboard = lazy(() => 
  import(/* webpackChunkName: "analytics" */ "./pages/AnalyticsDashboard")
);
const ReportBuilder = lazy(() => 
  import(/* webpackChunkName: "analytics" */ "./pages/ReportBuilder")
);
const Meetings = lazy(() => 
  import(/* webpackChunkName: "meetings" */ "./pages/Meetings")
);

// Lazy load admin pages
const AdminDashboard = lazy(() => 
  import(/* webpackChunkName: "admin" */ "./pages/admin/AdminDashboard")
);
const UserManagement = lazy(() => 
  import(/* webpackChunkName: "admin" */ "./pages/admin/UserManagement")
);
const TeamManagement = lazy(() => 
  import(/* webpackChunkName: "admin" */ "./pages/admin/TeamManagement")
);
const DepartmentManagement = lazy(() => 
  import(/* webpackChunkName: "admin" */ "./pages/admin/DepartmentManagement")
);
const QuickCheckResults = lazy(() =>
  import(/* webpackChunkName: "admin" */ "./pages/admin/QuickCheckResults")
);
const QuickCheckManagement = lazy(() =>
  import(/* webpackChunkName: "admin" */ "./pages/admin/QuickCheckManagement")
);
// Direct import to avoid lazy loading initialization issues
import QuickCheckReport from "./pages/admin/QuickCheckReport";
const QuickCheckDetails = lazy(() =>
  import(/* webpackChunkName: "admin" */ "./pages/admin/QuickCheckDetails")
);
const QuickCheckIADetails = lazy(() =>
  import(/* webpackChunkName: "admin" */ "./pages/admin/QuickCheckIADetails")
);

// Lazy load user pages
const Profile = lazy(() =>
  import(/* webpackChunkName: "user" */ "./pages/Profile")
);
const Settings = lazy(() =>
  import(/* webpackChunkName: "user" */ "./pages/Settings")
);
const Help = lazy(() =>
  import(/* webpackChunkName: "user" */ "./pages/Help")
);

// Lazy load legal pages
const Terms = lazy(() =>
  import(/* webpackChunkName: "legal" */ "./pages/Terms")
);
const Privacy = lazy(() =>
  import(/* webpackChunkName: "legal" */ "./pages/Privacy")
);

function App() {
  // Preload critical routes after initial render
  React.useEffect(() => {
    const timer = setTimeout(() => {
      // Prefetch dashboard after 2 seconds
      import("./components/dashboard/RoleBasedDashboard");
    }, 2000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <ErrorBoundary>
      <AuthProvider>
        <Router>
          <MetaTags />
          <Suspense fallback={<LoadingFallback />}>
            <Routes>
          {/* Public routes */}
          <Route path="/" element={<HomeSimple />} />
          <Route path="/sobre" element={<About />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/login" element={<Login />} />
          {/* Cadastro público desabilitado temporariamente - apenas BEATT_ADMIN pode cadastrar */}
          {/* <Route path="/cadastro" element={<Signup />} /> */}
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/termos" element={<Terms />} />
          <Route path="/privacidade" element={<Privacy />} />
          <Route
            path="/verificacao-email"
            element={<EmailVerification />}
          />
          <Route
            path="/quickcheck/:token"
            element={<QuickCheck />}
          />
          <Route
            path="/quickcheck/report/:token"
            element={<QuickCheckPublicReport />}
          />
          <Route
            path="/quickcheck/ia"
            element={<QuickCheckIA />}
          />
          <Route
            path="/quickcheck/ia/:token"
            element={<QuickCheckIA />}
          />
          <Route
            path="/quickcheck-ia/:publicToken"
            element={<QuickCheckIAPublicReport />}
          />

          {/* Test route */}
          <Route
            path="/test-auth"
            element={
              <ProtectedRoute>
                <TestAuth />
              </ProtectedRoute>
            }
          />

          {/* Protected routes */}
          <Route
            path="/onboarding"
            element={
              <ProtectedRoute>
                <Onboarding />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <RoleBasedDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/assessments"
            element={
              <ProtectedRoute>
                <Assessments />
              </ProtectedRoute>
            }
          />
          <Route
            path="/avaliacoes"
            element={
              <ProtectedRoute>
                <Assessments />
              </ProtectedRoute>
            }
          />
          <Route
            path="/avaliacao-criador"
            element={
              <ProtectedRoute>
                <AssessmentCreator />
              </ProtectedRoute>
            }
          />
          <Route
            path="/avaliacao-distribuir"
            element={
              <ProtectedRoute>
                <AssessmentDistribute />
              </ProtectedRoute>
            }
          />
          <Route
            path="/avaliacao-responder"
            element={
              <ProtectedRoute>
                <AssessmentTake />
              </ProtectedRoute>
            }
          />
          <Route
            path="/avaliacao-resultados"
            element={
              <ProtectedRoute>
                <AssessmentResultsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/okrs"
            element={
              <ProtectedRoute>
                <OKRManager />
              </ProtectedRoute>
            }
          />
          <Route
            path="/feedbacks"
            element={
              <ProtectedRoute>
                <FeedbackManager />
              </ProtectedRoute>
            }
          />
          {/* PDI Routes */}
          <Route
            path="/pdi"
            element={
              <ProtectedRoute>
                <PDI />
              </ProtectedRoute>
            }
          />
          <Route
            path="/pdi/new"
            element={
              <ProtectedRoute>
                <PDICreate />
              </ProtectedRoute>
            }
          />
          <Route
            path="/pdi/:id"
            element={
              <ProtectedRoute>
                <PDI />
              </ProtectedRoute>
            }
          />
          <Route
            path="/pdi/:id/:tab"
            element={
              <ProtectedRoute>
                <PDI />
              </ProtectedRoute>
            }
          />
          {/* Legacy PDI route */}
          <Route
            path="/pdi-manager"
            element={
              <ProtectedRoute>
                <PDIManager />
              </ProtectedRoute>
            }
          />
          <Route
            path="/feedback"
            element={
              <ProtectedRoute>
                <FeedbackManager />
              </ProtectedRoute>
            }
          />
          <Route
            path="/surveys"
            element={
              <ProtectedRoute>
                <SurveyManager />
              </ProtectedRoute>
            }
          />
          <Route
            path="/analytics"
            element={
              <ProtectedRoute>
                <AnalyticsDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports"
            element={
              <ProtectedRoute>
                <ReportBuilder />
              </ProtectedRoute>
            }
          />
          <Route
            path="/meetings"
            element={
              <ProtectedRoute>
                <Meetings />
              </ProtectedRoute>
            }
          />
          
          {/* Admin routes */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <AdminDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/administration"
            element={
              <ProtectedRoute>
                <AdminDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/users"
            element={
              <ProtectedRoute>
                <UserManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/teams"
            element={
              <ProtectedRoute>
                <TeamManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/departments"
            element={
              <ProtectedRoute>
                <DepartmentManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/quickchecks"
            element={
              <ProtectedRoute>
                <BeattAdminRoute>
                  <QuickCheckManagement />
                </BeattAdminRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/quickcheck-report/:surveyId/:responseId"
            element={
              <ProtectedRoute>
                <BeattAdminRoute>
                  <QuickCheckReport />
                </BeattAdminRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/quickcheck-details/:surveyId/:responseId"
            element={
              <ProtectedRoute>
                <BeattAdminRoute>
                  <QuickCheckDetails />
                </BeattAdminRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/quickcheck-ia-details/:token"
            element={
              <ProtectedRoute>
                <BeattAdminRoute>
                  <QuickCheckIADetails />
                </BeattAdminRoute>
              </ProtectedRoute>
            }
          />

          {/* User routes */}
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <Settings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/help"
            element={
              <ProtectedRoute>
                <Help />
              </ProtectedRoute>
            }
          />
            </Routes>
          </Suspense>
        </Router>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
